<template>
  <v-form ref="formRef" v-model="formValid" @submit.prevent>
    <v-expansion-panels v-model="expandedPanels" multiple>
      <v-expansion-panel
        v-for="section in processedSections"
        :key="section.id"
        :value="section.id"
      >
        <v-expansion-panel-title class="bg-primary text-white">
          <v-icon size="small" class="mr-2">{{ section.icon }}</v-icon>
          {{ tl(section.title) }}
          <span v-if="section.required" class="text-error ml-1">*</span>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <SectionRenderer
            :section="section"
            :form-data="formData"
            :readonly="props.readOnly"
            :ref-data="mergedRefData"
            :contacts="contacts"
            :contacts-loading="isLoadingContacts"
            density="comfortable"
            @update:field="handleFieldUpdate"
          />
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-alert type="info" variant="tonal" density="compact" class="mt-4">
      <span class="text-error font-weight-bold">*</span> {{ tl('indicates required fields') }}
    </v-alert>
  </v-form>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed, onMounted, nextTick } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { apiClient } from '@/shared/api/client';
import SectionRenderer from '../shared/renderers/SectionRenderer.vue';
import { useBoFormConfig } from './composables/useBoFormConfig';
import type { SectionDef, FieldDef } from './composables/useBoFormConfig';

// Translation
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage, { silent: true });
const tl = (text: string) => {
  if (!text) return '';
  const translated = t(`startTask.${text}`);
  return (translated && translated !== `startTask.${text}`) ? translated : text;
};

const { boFormConfig, sections, conditionalRules, validation, refData, boCategoriesMap, idTypeFormats, boCriteriaMap } = useBoFormConfig();

interface Props {
  taskConfig?: any;
  loading?: boolean;
  taskId?: string | number | null;
  readOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  taskConfig: null,
  loading: false,
  taskId: null,
  readOnly: false,
});

const emit = defineEmits<{
  (e: 'update:formData', data: any): void;
  (e: 'update:valid', valid: boolean): void;
}>();

const formRef = ref<any>(null);
const formValid = ref(false);
const isLoadingContacts = ref(false);
const lastAutoFilledContactCd = ref('');

interface Contact {
  contactCd?: string;
  contactName?: string;
  contactRegNo?: string;
  contactEmail?: string;
  dateOfBirth?: string | null;
  idTypeCd?: string;
  idTypeName?: string;
  mobileNo?: string;
  contactMobile?: string;
  phoneNo?: string;
  MOBILE_NO?: string;
  CONTACT_MOBILE?: string;
  PHONE_NO?: string;
}

const contacts = ref<Contact[]>([]);

const expandedPanels = ref(
  props.readOnly ? [] : ['appointment', 'dates', 'boInfo', 'criteria', 'residentialAddress', 'businessAddress']
);

const MONTH_NAMES = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];

function formatDateToDisplay(date: Date): string {
  return `${date.getDate()} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
}

function mapContactIdTypeToBoIdType(contact: Contact): string {
  const byCode: Record<string, string> = {
    M01004: '01',
    M01002: '03',
    M01006: '04',
    M01005: '05',
    M01007: '06',
    M01003: '08',
  };
  const byName: Record<string, string> = {
    NRIC: '01',
    'MALAYSIAN NRIC': '01',
    'ARMY ID': '03',
    'POLICE ID': '04',
    PASSPORT: '05',
    'PERMANENT RESIDENCE NUMBER': '06',
    MYPR: '06',
    MYKAS: '08',
    'TEMPORARY RESIDENCE': '08',
  };

  const code = (contact.idTypeCd || '').toUpperCase().trim();
  if (code && byCode[code]) return byCode[code];

  const name = (contact.idTypeName || '').toUpperCase().trim();
  if (name && byName[name]) return byName[name];
  if (name.includes('NRIC')) return '01';
  if (name.includes('ARMY')) return '03';
  if (name.includes('POLICE')) return '04';
  if (name.includes('PASSPORT')) return '05';
  if (name.includes('MYPR') || name.includes('PERMANENT')) return '06';
  if (name.includes('MYKAS') || name.includes('TEMPORARY')) return '08';
  return '';
}

function resolveContactPhone(contact: Contact): string {
  return (
    contact.mobileNo ||
    contact.contactMobile ||
    contact.phoneNo ||
    contact.MOBILE_NO ||
    contact.CONTACT_MOBILE ||
    contact.PHONE_NO ||
    ''
  ).toString();
}

async function loadContacts() {
  isLoadingContacts.value = true;
  try {
    const response = await apiClient.get('/startTask/common/officers');
    const data = (response as any)?.data || response;
    contacts.value = Array.isArray(data?.officers) ? data.officers : [];
  } catch (error) {
    console.error('[BeneficialOwnerForm] Failed to load contacts:', error);
    contacts.value = [];
  } finally {
    isLoadingContacts.value = false;
  }
}

// ===== Form data =====
const formData = reactive({
  // Appointment
  userType: 'BO',
  // Dates
  changeDt: '',
  recordedDt: '',
  // BO Info
  boType: 'I',
  category: 'I',
  idType: '01',
  idNo: '',
  dob: '',
  name: '',
  email: '',
  phoneNo: '',
  designation: '',
  citizenship: 'M',
  nationality: 'MALAYSIA',
  gender: '',
  race: '',
  // Criteria
  isJointArrangement: 'false',
  isJointShareholder: 'false',
  typeBo: '',
  // Criteria A/B (D/I/DI)
  critboA: false,
  criteriaADirectPercent: '',
  criteriaAIndirectPercent: '',
  critboB: false,
  criteriaBDirectPercent: '',
  criteriaBIndirectPercent: '',
  // Criteria C/D/E/F (C - Control)
  critboC: false,
  critboD: false,
  critboE: false,
  criteriaEPercent: '',
  critboF: false,
  criteriaFPercent: '',
  // Residential Address
  resIsLocal: true,
  resAdd1: '',
  resAdd2: '',
  resAdd3: '',
  resPostcode: '',
  resCity: '',
  resState: '',
  resCountry: '',
  resCityOverseas: '',
  resStateOverseas: '',
  // Business Address
  bizSameAsRes: false,
  bizIsLocal: true,
  bizAdd1: '',
  bizAdd2: '',
  bizAdd3: '',
  bizPostcode: '',
  bizCity: '',
  bizState: '',
  bizCountry: '',
  bizCityOverseas: '',
  bizStateOverseas: '',
});

// ===== Config-driven data (from SSM4U JSON files via useBoFormConfig) =====

// ===== Dynamic Category options by boType =====
const currentCategoryOptions = computed(() => {
  return boCategoriesMap[formData.boType] || [];
});

// Merge refData with dynamic boCategories + filtered cities by state + filtered citizenship by idType
const mergedRefData = computed(() => ({
  ...refData,
  boCategories: currentCategoryOptions.value,
  boCitizenship: filteredCitizenshipOptions.value,
  resCities: formData.resState
    ? (refData.cities as any[]).filter((c: any) => c.state === formData.resState)
    : (refData.cities as any[]),
  bizCities: formData.bizState
    ? (refData.cities as any[]).filter((c: any) => c.state === formData.bizState)
    : (refData.cities as any[]),
}));

// ===== ID Type → Citizenship / Nationality / Race 매핑 =====
const idTypeCitizenshipMap: Record<string, { citizenship: string; nationality: string }> = {
  '01': { citizenship: 'M',  nationality: 'MALAYSIA' },  // NRIC
  '03': { citizenship: 'M',  nationality: 'MALAYSIA' },  // ARMY ID
  '04': { citizenship: 'M',  nationality: 'MALAYSIA' },  // POLICE ID
  '05': { citizenship: 'NM', nationality: '' },           // PASSPORT
  '06': { citizenship: 'PR', nationality: '' },           // MyPR
  '07': { citizenship: 'M',  nationality: 'MALAYSIA' },  // EXEMPTED
  '08': { citizenship: 'NM', nationality: '' },           // MyKAS
  '09': { citizenship: 'M',  nationality: 'MALAYSIA' },  // NATIONAL ID
};

// SSM4U 서버 제약: ID Type별 허용 Citizenship (서버에서 거부하는 조합 방지)
const idTypeAllowedCitizenship: Record<string, string[]> = {
  '01': ['M'],       // NRIC → Malaysian only
  '03': ['M'],       // ARMY ID → Malaysian only
  '04': ['M'],       // POLICE ID → Malaysian only
  '05': ['NM'],      // PASSPORT → Non-Malaysian only
  '06': ['PR'],      // MyPR → Permanent Resident only
  '07': ['M'],       // EXEMPTED → Malaysian only
  '08': ['NM'],      // MyKAS → Non-Malaysian only
  '09': ['M'],       // NATIONAL ID → Malaysian only
};

// ID Type에 따라 Citizenship 드롭다운 옵션 필터링
const filteredCitizenshipOptions = computed(() => {
  const allowed = idTypeAllowedCitizenship[formData.idType];
  if (!allowed) return refData.boCitizenship as any[];
  return (refData.boCitizenship as any[]).filter((opt: any) => allowed.includes(opt.value));
});

const citizenshipAutoSet = computed(() => !!idTypeCitizenshipMap[formData.idType]);
const citizenshipHintByIdType = computed(() => {
  const labels: Record<string, string> = {
    '01': 'NRIC → MALAYSIAN', '03': 'ARMY ID → MALAYSIAN', '04': 'POLICE ID → MALAYSIAN',
    '05': 'PASSPORT → NON-MALAYSIAN', '06': 'MyPR → PERMANENT RESIDENT',
    '07': 'EXEMPTED → MALAYSIAN', '08': 'MyKAS → NON-MALAYSIAN', '09': 'NATIONAL ID → MALAYSIAN',
  };
  return labels[formData.idType] || '';
});

// ===== processedSections: 동적 필드 제어 =====
const processedSections = computed((): SectionDef[] => {
  const isJointYes = formData.isJointArrangement === 'true';
  const typeBo = formData.typeBo;
  const idFmt = idTypeFormats[formData.idType];

  return sections.map((section: SectionDef) => {
    // --- businessAddress section: compound visibility (bizSameAsRes + bizIsLocal) ---
    if (section.id === 'businessAddress' && section.fields) {
      const isSameAsRes = !!formData.bizSameAsRes;
      const isBizLocal = !!formData.bizIsLocal;
      const LOCAL_FIELDS = ['bizState', 'bizCity', 'bizPostcode'];
      const OVERSEAS_FIELDS = ['bizCountry'];

      return {
        ...section,
        fields: section.fields.filter((field: FieldDef) => {
          if (field.id === 'bizSameAsRes') return true;
          if (isSameAsRes) return false;
          if (field.id === 'bizIsLocal') return true;
          if (['bizAdd1', 'bizAdd2', 'bizAdd3'].includes(field.id as string)) return true;
          if (LOCAL_FIELDS.includes(field.id as string)) return isBizLocal;
          if (OVERSEAS_FIELDS.includes(field.id as string)) return !isBizLocal;
          return true;
        }),
      };
    }

    // --- boInfo section ---
    if (section.id === 'boInfo' && section.fields) {
      return {
        ...section,
        fields: section.fields.map((field: FieldDef) => {
          // Category: 동적 dataSource (boType에 따라 옵션 변경)
          if (field.id === 'category') {
            const opts = currentCategoryOptions.value;
            if (opts.length <= 1) {
              // 단일 옵션 → disabled + 자동 선택
              return { ...field, disabled: true, hint: `${tl('Auto-set for')} ${formData.boType}` };
            }
            return field;
          }
          // idNo: ID Type에 따라 maxlength/placeholder/hint 동적 변경
          if (field.id === 'idNo' && idFmt) {
            return {
              ...field,
              maxlength: idFmt.maxlength || 200,
              placeholder: idFmt.placeholder || '',
              hint: idFmt.hint || '',
            };
          }
          // Citizenship: ID Type에 의해 제한됨 (선택지 1개면 disabled)
          if (field.id === 'citizenship' && citizenshipAutoSet.value) {
            const singleOption = filteredCitizenshipOptions.value.length <= 1;
            return {
              ...field,
              disabled: singleOption,
              hint: `${tl('Restricted by Id Type')} (${citizenshipHintByIdType.value})`,
            };
          }
          // Nationality: hint when Malaysian (editable)
          if (field.id === 'nationality' && formData.citizenship === 'M') {
            return { ...field, hint: `MALAYSIAN → MALAYSIA (${tl('auto-set')})` };
          }
          // Race: hint when Non-Malaysian/PR (editable)
          if (field.id === 'race' && (formData.citizenship === 'NM' || formData.citizenship === 'PR')) {
            return { ...field, hint: `${formData.citizenship === 'NM' ? 'NON-MALAYSIAN' : 'PERMANENT RESIDENT'} → FOREIGNER (${tl('auto-set')})` };
          }
          return field;
        }),
      };
    }

    // --- criteria section ---
    if (section.id !== 'criteria' || !section.fields) return section;

    return {
      ...section,
      fields: section.fields.filter((field: FieldDef) => {
        // Criteria A Direct%: D/DI만
        if (field.id === 'criteriaADirectPercent' && !['D', 'DI'].includes(typeBo)) return false;
        // Criteria A Indirect%: I/DI만
        if (field.id === 'criteriaAIndirectPercent' && !['I', 'DI'].includes(typeBo)) return false;
        // Criteria B Direct%: D/DI만
        if (field.id === 'criteriaBDirectPercent' && !['D', 'DI'].includes(typeBo)) return false;
        // Criteria B Indirect%: I/DI만
        if (field.id === 'criteriaBIndirectPercent' && !['I', 'DI'].includes(typeBo)) return false;
        return true;
      }).map((field: FieldDef) => {
        // criteriaRef → label 동적 해석 (bo-criteria.json 참조)
        // A/B는 typeBo에 따라 설명이 달라짐 (descByTypeBo)
        const ref = (field as any).criteriaRef as string | undefined;
        if (ref && boCriteriaMap[ref]) {
          const c = boCriteriaMap[ref] as any;
          const desc = (c.descByTypeBo && typeBo && c.descByTypeBo[typeBo]) || c.desc;
          field = { ...field, label: `${c.label} - ${desc}` };
        }
        // Joint Arrangement = YES → typeBo disabled
        if (field.id === 'typeBo' && isJointYes) {
          return { ...field, disabled: true, hint: tl('Locked to CONTROL BY OTHER MEANS (Joint Arrangement = Yes)') };
        }
        // JA=Yes → Criteria E auto-checked, 나머지 criteria disabled
        if (isJointYes) {
          if (field.id === 'critboE') {
            return { ...field, disabled: true, hint: tl('Auto-checked by Joint Arrangement = Yes') };
          }
          if (['critboC', 'critboD', 'critboF', 'criteriaEPercent'].includes(field.id as string)) {
            return { ...field, disabled: true, hint: tl('Disabled when Joint Arrangement = Yes') };
          }
        }
        // JA=No → Criteria E always disabled (SSM policy: E only available via Joint Arrangement = Yes)
        if (!isJointYes && field.id === 'critboE') {
          return { ...field, disabled: true, hint: tl('Criteria E is only available via Joint Arrangement = Yes') };
        }
        return field;
      }),
    };
  });
});

// ===== Watchers =====

// Emit formData changes
watch(formData, (newVal) => {
  emit('update:formData', { ...newVal });
}, { deep: true });

watch(formValid, (newVal) => {
  emit('update:valid', newVal);
});

// --- boType 변경 → category 자동 설정 ---
watch(() => formData.boType, (newVal) => {
  const opts = boCategoriesMap[newVal];
  if (opts && opts.length > 0) {
    // 현재 category가 새 옵션에 없으면 첫번째로 리셋
    const validValues = opts.map((o: any) => o.value);
    if (!validValues.includes(formData.category)) {
      formData.category = opts[0].value ?? '';
    }
  }
});

// --- idType 변경 → citizenship/nationality/race + idNo 초기화 ---
watch(() => formData.idType, (newVal) => {
  const mapping = idTypeCitizenshipMap[newVal];
  if (mapping) {
    formData.citizenship = mapping.citizenship;
    formData.nationality = mapping.nationality;
    // Race 자동 설정: NM/PR → FOREIGNER, Malaysian → 사용자 선택 허용
    if (mapping.citizenship === 'NM' || mapping.citizenship === 'PR') {
      formData.race = 'F'; // FOREIGNER
    } else {
      formData.race = ''; // Malaysian → 사용자가 직접 선택
    }
  }
  // idNo 초기화 (포맷 변경 시)
  // contact auto-fill로 인한 idType 변경에서는 idNo를 유지한다.
  if (suppressIdNoResetByIdType) {
    suppressIdNoResetByIdType = false;
  } else {
    formData.idNo = '';
  }
});

watch(() => formData.name, (newVal) => {
  const name = (newVal || '').toString().trim();
  if (!name || !contacts.value.length) {
    lastAutoFilledContactCd.value = '';
    return;
  }

  const normalizedName = name.toUpperCase();
  const matched = contacts.value.find((c) => (c.contactName || '').toUpperCase().trim() === normalizedName);
  if (!matched) {
    lastAutoFilledContactCd.value = '';
    return;
  }

  const contactKey = matched.contactCd || matched.contactRegNo || normalizedName;
  if (contactKey === lastAutoFilledContactCd.value) return;

  const mappedIdType = mapContactIdTypeToBoIdType(matched);
  if (mappedIdType) {
    suppressIdNoResetByIdType = true;
    formData.idType = mappedIdType;
  }
  if (matched.contactRegNo) formData.idNo = matched.contactRegNo.toUpperCase().trim();
  if (matched.contactEmail) formData.email = matched.contactEmail.trim();

  const phone = resolveContactPhone(matched);
  if (phone) formData.phoneNo = phone.trim();

  if (matched.dateOfBirth) {
    const dob = new Date(matched.dateOfBirth);
    if (!isNaN(dob.getTime())) {
      formData.dob = formatDateToDisplay(dob);
    }
  }

  lastAutoFilledContactCd.value = contactKey;
});

// --- Joint Arrangement → typeBo + Criteria E ---
watch(() => formData.isJointArrangement, (newVal) => {
  if (newVal === 'true') {
    formData.typeBo = 'C';
    // JA=Yes → auto-check Criteria E, clear others
    formData.critboC = false;
    formData.critboD = false;
    formData.critboE = true;
    formData.critboF = false;
    formData.criteriaFPercent = '';
  } else {
    // JA=No → uncheck Criteria E
    formData.critboE = false;
    formData.criteriaEPercent = '';
  }
});

// --- typeBo 변경 → criteria 초기화 ---
watch(() => formData.typeBo, (newVal, oldVal) => {
  if (newVal === oldVal) return;

  if (newVal === 'C') {
    // → Control: clear A/B
    formData.critboA = false;
    formData.criteriaADirectPercent = '';
    formData.criteriaAIndirectPercent = '';
    formData.critboB = false;
    formData.criteriaBDirectPercent = '';
    formData.criteriaBIndirectPercent = '';
  } else {
    // → D/I/DI: clear C/D/E/F
    formData.critboC = false;
    formData.critboD = false;
    formData.critboE = false;
    formData.criteriaEPercent = '';
    formData.critboF = false;
    formData.criteriaFPercent = '';
    // type별 불필요 % 필드 초기화
    if (newVal === 'D') {
      formData.criteriaAIndirectPercent = '';
      formData.criteriaBIndirectPercent = '';
    } else if (newVal === 'I') {
      formData.criteriaADirectPercent = '';
      formData.criteriaBDirectPercent = '';
    }
  }
});

// --- Address cascading auto-fill (like DI AddressBlock) ---
// Guard flag to prevent circular watcher triggers
let resAutoFillSource = '';
let bizAutoFillSource = '';
let suppressIdNoResetByIdType = false;

// Direction 1: Postcode → State + City (user types postcode)
watch(() => formData.resPostcode, (newVal) => {
  if (resAutoFillSource === 'city' || resAutoFillSource === 'state') return;
  if (!newVal || newVal.length !== 5 || !formData.resIsLocal) return;
  const allPostcodes = refData.postcodes as any[];
  if (!allPostcodes?.length) return;
  const matches = allPostcodes.filter((p: any) => p.label === newVal);
  if (matches.length > 0) {
    resAutoFillSource = 'postcode';
    formData.resState = matches[0].state || '';
    // If only one city for this postcode, auto-set. Otherwise keep current if valid.
    if (matches.length === 1) {
      formData.resCity = matches[0].city || '';
    } else {
      const matchCities = matches.map((m: any) => m.city);
      if (!matchCities.includes(formData.resCity)) {
        formData.resCity = '';
      }
    }
    nextTick(() => { resAutoFillSource = ''; });
  }
});

// Direction 2: State → clear City, Postcode (cascading reset)
watch(() => formData.resState, (newVal, oldVal) => {
  if (resAutoFillSource === 'postcode') return;
  if (newVal === oldVal) return;
  resAutoFillSource = 'state';
  formData.resCity = '';
  formData.resPostcode = '';
  nextTick(() => { resAutoFillSource = ''; });
});

// Direction 2: City → auto-fill Postcode (if single match)
watch(() => formData.resCity, (newVal) => {
  if (resAutoFillSource === 'postcode' || resAutoFillSource === 'state') return;
  if (!newVal || !formData.resIsLocal) return;
  const allPostcodes = refData.postcodes as any[];
  if (!allPostcodes?.length) return;
  const matching = allPostcodes.filter((p: any) => p.city === newVal);
  if (matching.length === 1) {
    resAutoFillSource = 'city';
    formData.resPostcode = matching[0].label;
    nextTick(() => { resAutoFillSource = ''; });
  }
});

// Business address: same pattern
watch(() => formData.bizPostcode, (newVal) => {
  if (bizAutoFillSource === 'city' || bizAutoFillSource === 'state') return;
  if (!newVal || newVal.length !== 5 || formData.bizSameAsRes || !formData.bizIsLocal) return;
  const allPostcodes = refData.postcodes as any[];
  if (!allPostcodes?.length) return;
  const matches = allPostcodes.filter((p: any) => p.label === newVal);
  if (matches.length > 0) {
    bizAutoFillSource = 'postcode';
    formData.bizState = matches[0].state || '';
    if (matches.length === 1) {
      formData.bizCity = matches[0].city || '';
    } else {
      const matchCities = matches.map((m: any) => m.city);
      if (!matchCities.includes(formData.bizCity)) {
        formData.bizCity = '';
      }
    }
    nextTick(() => { bizAutoFillSource = ''; });
  }
});

watch(() => formData.bizState, (newVal, oldVal) => {
  if (bizAutoFillSource === 'postcode') return;
  if (newVal === oldVal) return;
  bizAutoFillSource = 'state';
  formData.bizCity = '';
  formData.bizPostcode = '';
  nextTick(() => { bizAutoFillSource = ''; });
});

watch(() => formData.bizCity, (newVal) => {
  if (bizAutoFillSource === 'postcode' || bizAutoFillSource === 'state') return;
  if (!newVal || formData.bizSameAsRes || !formData.bizIsLocal) return;
  const allPostcodes = refData.postcodes as any[];
  if (!allPostcodes?.length) return;
  const matching = allPostcodes.filter((p: any) => p.city === newVal);
  if (matching.length === 1) {
    bizAutoFillSource = 'city';
    formData.bizPostcode = matching[0].label;
    nextTick(() => { bizAutoFillSource = ''; });
  }
});

// --- bizSameAsRes → copy residential ---
watch(() => formData.bizSameAsRes, (newVal) => {
  if (newVal) {
    formData.bizIsLocal = formData.resIsLocal;
    formData.bizAdd1 = formData.resAdd1;
    formData.bizAdd2 = formData.resAdd2;
    formData.bizAdd3 = formData.resAdd3;
    formData.bizPostcode = formData.resPostcode;
    formData.bizCity = formData.resCity;
    formData.bizState = formData.resState;
    formData.bizCountry = formData.resCountry;
    formData.bizCityOverseas = formData.resCityOverseas;
    formData.bizStateOverseas = formData.resStateOverseas;
  }
});

// ===== Field update handler =====
const handleFieldUpdate = (payload: { sectionId: string; fieldId: string; value: any }) => {
  const { fieldId, value } = payload;
  if (fieldId in formData) {
    (formData as any)[fieldId] = value;
  }
};

// ===== Validation =====
const validationErrors = ref<string[]>([]);

const validate = async (): Promise<boolean> => {
  validationErrors.value = [];

  let formValidResult = true;
  if (formRef.value) {
    const result = await formRef.value.validate();
    formValidResult = result.valid;
  }

  // Required fields
  if (!formData.changeDt) validationErrors.value.push(tl('Date of Becoming BO is required'));
  if (!formData.recordedDt) validationErrors.value.push(tl('Date of Data Recorded is required'));
  if (!formData.boType) validationErrors.value.push(tl('Type is required'));
  if (!formData.category) validationErrors.value.push(tl('Category is required'));
  if (!formData.idType) validationErrors.value.push(tl('Id Type is required'));
  if (!formData.idNo?.trim()) validationErrors.value.push(tl('Identification No. is required'));
  if (!formData.name?.trim()) validationErrors.value.push(tl('Name is required'));
  if (!formData.email?.trim()) validationErrors.value.push(tl('Email is required'));
  if (!formData.phoneNo?.trim()) validationErrors.value.push(tl('Phone No. is required'));
  if (!formData.designation?.trim()) validationErrors.value.push(tl('Designation is required'));
  if (!formData.citizenship) validationErrors.value.push(tl('Citizenship is required'));
  if (!formData.nationality) validationErrors.value.push(tl('Nationality is required'));

  // ID Type ↔ Citizenship 교차 검증 (SSM4U 서버 제약)
  const allowedCitizenship = idTypeAllowedCitizenship[formData.idType];
  if (formData.citizenship && allowedCitizenship && !allowedCitizenship.includes(formData.citizenship)) {
    const idLabel = idTypeFormats[formData.idType]?.label || formData.idType;
    const allowedLabels = allowedCitizenship.map((c: string) => {
      const opt = (refData.boCitizenship as any[]).find((o: any) => o.value === c);
      return opt?.label || c;
    }).join(', ');
    validationErrors.value.push(tl(`${idLabel} requires citizenship: ${allowedLabels}`));
  }

  // Nationality 교차 검증
  if (formData.idType === '05' && formData.nationality === 'MALAYSIA') {
    validationErrors.value.push(tl('Passport holders cannot select Malaysia as nationality'));
  } else if ((formData.citizenship === 'NM' || formData.citizenship === 'PR') && formData.nationality === 'MALAYSIA') {
    validationErrors.value.push(tl('Non-Malaysian/PR citizens cannot select Malaysia as nationality'));
  }
  if (!formData.gender) validationErrors.value.push(tl('Gender is required'));
  if (!formData.race) validationErrors.value.push(tl('Race is required'));
  if (!formData.typeBo) validationErrors.value.push(tl('Types of BO is required'));

  // NRIC format: 12 digits
  if (formData.idType === '01' && formData.idNo) {
    if (!/^\d{12}$/.test(formData.idNo.replace(/[-\s]/g, ''))) {
      validationErrors.value.push(tl('NRIC must be 12 digits (no dashes)'));
    }
  }

  // Email
  if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    validationErrors.value.push(tl('Please enter a valid email address'));
  }

  // Phone
  if (formData.phoneNo && formData.phoneNo.length > 15) {
    validationErrors.value.push(tl('Phone No. must be 15 characters or less'));
  }

  // Residential Address
  if (!formData.resAdd1?.trim()) validationErrors.value.push(tl('Residential Address Line 1 is required'));
  if (formData.resIsLocal) {
    if (!formData.resPostcode?.trim()) validationErrors.value.push(tl('Residential Postcode is required'));
  } else {
    if (!formData.resCountry) validationErrors.value.push(tl('Residential Country is required'));
  }

  // Business Address (optional - validate only if user entered data)
  if (!formData.bizSameAsRes && formData.bizAdd1?.trim()) {
    if (formData.bizIsLocal) {
      if (!formData.bizPostcode?.trim()) validationErrors.value.push(tl('Business Postcode is required when address is provided'));
    } else {
      if (!formData.bizCountry) validationErrors.value.push(tl('Business Country is required for overseas address'));
    }
  }

  // Criteria: D/I/DI → at least A or B
  if (['D', 'I', 'DI'].includes(formData.typeBo)) {
    if (!formData.critboA && !formData.critboB) {
      validationErrors.value.push(tl('At least one Criteria (A or B) must be selected'));
    }
    if (formData.critboA) {
      if (['D', 'DI'].includes(formData.typeBo)) {
        if (!formData.criteriaADirectPercent) {
          validationErrors.value.push(tl('Direct Ownership (%) is required when Criteria A is selected'));
        } else if (parseFloat(formData.criteriaADirectPercent) < 20) {
          validationErrors.value.push(tl('Criteria A Direct Ownership must be at least 20%'));
        }
      }
      if (['I', 'DI'].includes(formData.typeBo)) {
        if (!formData.criteriaAIndirectPercent) {
          validationErrors.value.push(tl('Indirect Ownership (%) is required when Criteria A is selected'));
        } else if (parseFloat(formData.criteriaAIndirectPercent) < 20) {
          validationErrors.value.push(tl('Criteria A Indirect Ownership must be at least 20%'));
        }
      }
    }
    if (formData.critboB) {
      if (['D', 'DI'].includes(formData.typeBo) && !formData.criteriaBDirectPercent) {
        validationErrors.value.push(tl('Direct Voting Shares (%) is required when Criteria B is selected'));
      }
      if (['I', 'DI'].includes(formData.typeBo) && !formData.criteriaBIndirectPercent) {
        validationErrors.value.push(tl('Indirect Voting Shares (%) is required when Criteria B is selected'));
      }
    }
  }

  // Criteria: C → at least C/D/F (JA=Yes → E is auto, skip check)
  if (formData.typeBo === 'C' && formData.isJointArrangement !== 'true') {
    if (!formData.critboC && !formData.critboD && !formData.critboF) {
      validationErrors.value.push(tl('At least one Criteria (C, D, or F) must be selected'));
    }
  }
  if (formData.critboF && !formData.criteriaFPercent) {
    validationErrors.value.push(tl('Voting Shares (%) is required when Criteria F is selected'));
  }

  if (validationErrors.value.length > 0) return false;
  return formValidResult;
};

const getValidationErrors = () => validationErrors.value;
const getFormData = () => ({ ...formData });

const resetForm = () => {
  Object.assign(formData, {
    userType: 'BO', changeDt: '', recordedDt: '',
    boType: 'I', category: 'I', idType: '01', idNo: '', dob: '',
    name: '', email: '', phoneNo: '', designation: '',
    citizenship: 'M', nationality: 'MALAYSIA', gender: '', race: '',
    isJointArrangement: 'false', isJointShareholder: 'false', typeBo: '',
    critboA: false, criteriaADirectPercent: '', criteriaAIndirectPercent: '',
    critboB: false, criteriaBDirectPercent: '', criteriaBIndirectPercent: '',
    critboC: false, critboD: false, critboE: false, criteriaEPercent: '',
    critboF: false, criteriaFPercent: '',
    resIsLocal: true, resAdd1: '', resAdd2: '', resAdd3: '',
    resPostcode: '', resCity: '', resState: '', resCountry: '',
    resCityOverseas: '', resStateOverseas: '',
    bizSameAsRes: false, bizIsLocal: true, bizAdd1: '', bizAdd2: '', bizAdd3: '',
    bizPostcode: '', bizCity: '', bizState: '', bizCountry: '', bizCityOverseas: '', bizStateOverseas: '',
  });
  formRef.value?.resetValidation();
  expandedPanels.value = props.readOnly ? [] : ['appointment', 'dates', 'boInfo', 'criteria', 'residentialAddress', 'businessAddress'];
};

const loadSavedData = (savedData: any) => {
  if (!savedData) return;
  Object.keys(savedData).forEach(key => {
    if (key in formData) {
      (formData as any)[key] = savedData[key];
    }
  });
};

onMounted(async () => {
  await loadContacts();
  await nextTick();
  window.scrollTo({ top: 0, behavior: 'smooth' });
});

defineExpose({
  validate,
  getValidationErrors,
  getFormData,
  resetForm,
  loadSavedData,
});
</script>

<style scoped>
.text-uppercase :deep(input) {
  text-transform: uppercase;
}

:deep(.v-expansion-panel) {
  margin-bottom: 8px;
}

:deep(.v-expansion-panel-title) {
  min-height: 48px;
}
</style>
